import { DocumentStatus, type ExternalJustificatif } from '@cpce/shared';
import { ArrowUpRight, FileText } from 'lucide-react';
import type { PropsWithChildren } from 'react';
import { Badge, BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { CardWithTitle } from '@/components/CardWithTitle';
import { DocumentViewerButton } from '@/components/DocumentViewerButton';
import { InfoCard } from '@/components/InfoCard';
import { ValidationStatusBadge } from '@/components/ValidationStatusBadge';
import { cn } from '@/utils/style';
import type { BeneficiaryFormatted } from '../beneficiaries.types';

interface BeneficiaryDocumentsCardProps {
  title: string;
  beneficiary: BeneficiaryFormatted;
  justificatifs: ExternalJustificatif[];
  onDocumentStatusChange?: (justificatif: ExternalJustificatif, validated: boolean) => void;
  hasAlert?: boolean;
  redirect?: {
    text: string;
    onRedirect: () => void;
  };
  titleActions?: React.ReactNode;
}

export function BeneficiaryDocumentsCard(props: PropsWithChildren<BeneficiaryDocumentsCardProps>) {
  const {
    title,
    beneficiary,
    justificatifs = [],
    onDocumentStatusChange,
    redirect,
    hasAlert: initialHasAlert,
    titleActions,
    children,
  } = props;
  const hasAlert =
    initialHasAlert ||
    justificatifs.some((doc) => doc.status === DocumentStatus.awaitingValidation);

  return (
    <CardWithTitle
      key={beneficiary.id}
      title={title}
      hasAlert={hasAlert}
      titleActions={titleActions}
      actions={
        <>
          <Badge icon={<FileText />} color={BadgeColor.GRAY}>
            {justificatifs.length} documents
          </Badge>
          {redirect && (
            <Button className="text-purple-600" onClick={redirect.onRedirect}>
              {redirect.text} <ArrowUpRight />
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-2">
        {children}
        <div className={cn('flex flex-col gap-2 border-gray-200', children && 'pt-4 border-t')}>
          {justificatifs.map((justificatif) => (
            <JustificatifCard
              key={justificatif.id}
              justificatif={justificatif}
              onStatusChange={
                onDocumentStatusChange
                  ? (validated) => onDocumentStatusChange(justificatif, validated)
                  : undefined
              }
            />
          ))}
        </div>
      </div>
    </CardWithTitle>
  );
}

interface JustificatifCardProps {
  justificatif: ExternalJustificatif;
  onStatusChange?: (validated: boolean) => void;
}

function JustificatifCard(props: JustificatifCardProps) {
  const { justificatif, onStatusChange } = props;
  const status = justificatif.status;

  return (
    <InfoCard
      icon={FileText}
      title={justificatif.type}
      header={
        <div className="flex grow justify-between">
          <ValidationStatusBadge status={status} />
          <div className="flex items-center gap-2">
            {status === DocumentStatus.awaitingValidation && onStatusChange && (
              <>
                <Button
                  variant="color"
                  className="bg-green-600"
                  onClick={() => onStatusChange(true)}
                >
                  Valider
                </Button>
                <Button
                  variant="color"
                  className="bg-red-600"
                  onClick={() => onStatusChange(false)}
                >
                  Refuser
                </Button>
              </>
            )}
            <DocumentViewerButton justificatif={justificatif} />
          </div>
        </div>
      }
      color={
        status === DocumentStatus.awaitingValidation
          ? 'orange'
          : status === DocumentStatus.refused
            ? 'red'
            : 'green'
      }
    />
  );
}
